import React from 'react';

import { useDragLayer, XYCoord } from 'react-dnd';

import { DragAndDropContext } from '../../contexts';
import * as constants from '../DaySlot/EventPlank/constants';
import { EventPlankLayout } from '../DaySlot/EventPlank/EventPlankLayout';
import * as t from './DragLayer.types';

const layerStyles: React.CSSProperties = {
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 0,
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
};

function getItemStyles(
  initialPosition: XYCoord,
  initialOffset: XYCoord | null,
  currentOffset: XYCoord | null,
): React.CSSProperties {
  if (!initialOffset || !currentOffset) {
    return { display: 'none' };
  }

  const x = initialPosition.x + currentOffset.x - initialOffset.x;
  const y = initialPosition.y + currentOffset.y - initialOffset.y;
  const transform = `translate(${x}px, ${y}px)`;

  return {
    transform,
    WebkitTransform: transform,
    position: 'absolute',
  };
}

export const DragLayer = (props: t.Props) => {
  const { selectedEvents } = React.useContext(DragAndDropContext.context);
  const {
    itemType,
    isDragging,
    item: draggedEvent,
    initialOffset,
    currentOffset,
  } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  const [eventData, setEventData] = React.useState<t.EventData[]>([]);

  React.useEffect(() => {
    if (!isDragging || itemType !== constants.dragAndDrop.EVENT_PLANK_DEFAULT) return;

    const events = selectedEvents.length > 0 ? selectedEvents : [draggedEvent.event];
    const data = events
      .map((event) => {
        const el: HTMLElement | null = document.querySelector(`div[data-plank-tail-id="${event.id}"]`);
        if (!el) return null;

        const width = el.offsetWidth || 0;
        const { x, y } = el.getBoundingClientRect();

        return {
          id: event.id,
          width,
          initialPosition: { x, y },
          period: props.periods.find((row) => row.some((day) => day.dayNumber === event.daysRange.start)) || [],
        };
      })
      .filter(Boolean) as t.EventData[];

    setEventData(data);
  }, [isDragging]);

  const renderEvent = (eventData: t.EventData, isPlaceholder: boolean) => {
    const { id, width, initialPosition } = eventData;
    return (
      <div style={{ width, ...getItemStyles(initialPosition, initialOffset, currentOffset) }} key={id}>
        <EventPlankLayout
          event={selectedEvents.find((e) => e.id === id) || draggedEvent.event}
          calendarDay={draggedEvent.calendarDay}
          period={eventData.period}
          planksStyles={{
            width: '100%',
            pointerEvents: 'none',
          }}
          isSelected={selectedEvents.length > 0}
          isPlaceholder={isPlaceholder}
        />
      </div>
    );
  };

  if (!isDragging || eventData.length === 0 || itemType !== constants.dragAndDrop.EVENT_PLANK_DEFAULT) return null;

  return <div style={layerStyles}>{eventData.map((data) => renderEvent(data, true))}</div>;
};
